import { Component, Inject, Input, OnInit, inject } from '@angular/core';
import { CONFIG } from '@nano/core/config';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { AccountType, BeneficiaryAddressSuggestion, PaymentAdditionalInfo, PurposeCode, RoutingCodes, SourceOfIncome } from './payment-additional-info';
import { PaymentStatus, PaymentType } from '../payment/payment';

@Component({
  selector: 'app-payment-additional-info',
  templateUrl: 'payment-additional-info.component.html',
  styleUrls: ['./payment-additional-info.component.less'],
  standalone: false
})
export class PaymentAdditionalInfoComponent implements OnInit {
  private readonly appConfig = inject(CONFIG);

  @Input() payment: PaymentAdditionalInfo;
  @Input() suggestedBeneficiaryAddress: BeneficiaryAddressSuggestion;
  @Input() paymentStatus: PaymentStatus;
  @Input() paymentOperationType: PaymentType;

  public SourceOfIncome = SourceOfIncome;
  public AccountType = AccountType;
  public PurposeCode = PurposeCode;
  public RoutingCodes = RoutingCodes;
  public PaymentStatus = PaymentStatus;
  public collapseActive = false;

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private translationService: L10nTranslationService
  ) {}

  ngOnInit(): void {
    this.collapseActive = this.hasDifferentSuggestions();
  }

  isTranslatable(value: string, enumType: object): boolean {
    return Object.values(enumType).includes(value);
  }

  isValidatingAddress(): boolean {
    return this.paymentStatus === PaymentStatus.VALIDATING_ADDRESS;
  }

  shouldShowSuggestion(userValue: string, suggestedValue: string): boolean {
    return this.isValidatingAddress() && !!suggestedValue && userValue !== suggestedValue;
  }

  hasDifferentSuggestions(): boolean {
    if (!this.isValidatingAddress() || !this.suggestedBeneficiaryAddress?.address) {
      return false;
    }

    const address = this.suggestedBeneficiaryAddress.address;

    return (
      this.shouldShowSuggestion(this.payment.beneficiaryCountry, address.country) ||
      this.shouldShowSuggestion(this.payment.beneficiaryProvince, address.countrySubDivision) ||
      this.shouldShowSuggestion(this.payment.beneficiaryCity, address.city) ||
      this.shouldShowSuggestion(this.payment.beneficiaryStreetName, address.street) ||
      this.shouldShowSuggestion(this.payment.beneficiaryBuildingNumber, address.buildingNumber) ||
      this.shouldShowSuggestion(this.payment.beneficiaryBuildingName, address.buildingName) ||
      this.shouldShowSuggestion(this.payment.beneficiaryAddress, address.addressLine) ||
      this.shouldShowSuggestion(this.payment.beneficiaryPostalCode, address.postCode)
    );
  }

  get correspondentBankCodeLabel(): string {
    const key = this.shouldUseSwiftLabel() ? 'payment.beneficiaryBankIdentifier.label' : 'payment.receiverInfo.correspondentBank.code.label';
    const label = this.translate(key);
    return label === key ? this.translate('payment.receiverInfo.correspondentBank.code.label') : label;
  }

  private shouldUseSwiftLabel(): boolean {
    return (
      this.appConfig.swiftPaymentBicNamingAndPostalCodeInfoEnabled &&
      (this.paymentOperationType === PaymentType.SWIFT ||
        this.paymentOperationType === PaymentType.SWIFT_XML ||
        this.paymentOperationType === PaymentType.TARGET2)
    );
  }

  private translate(key: string): string {
    return this.translationService.translate(key, this.locale.language) as string;
  }
}
